import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { BasketDetails, BasketVoucher } from 'shared-types/Basket';
import { Property } from 'shared-types/Property';
import type { BasketTemplateProps } from 'voyager';
import { formatCurrency } from 'voyager';
import { applyPromoCode, clearPromoCodeFailure, fetchBasketDetails } from '@store/basket/actions';
import { basketSelectors } from '@store/basket/selectors';
import { entitySelectors } from '@store/entity/selectors';
import { useApplyPromoCode } from '@features/discounts/components/PromoCodeForm/useApplyPromoCode';
import { useApplyVoucherCode } from '@features/discounts/components/VoucherCodeForm/useApplyVoucherCode';
import { useRemoveVoucherCode } from '@features/discounts/components/VoucherCodeForm/useRemoveVoucherCode';
import { TranslationKey, useTranslations } from '@lang';

type DiscountCodeProps = NonNullable<BasketTemplateProps['promoCode']>;
type AppState = { basket: { basketDetails: BasketDetails } } & Record<string, unknown>;

const EMPTY_VOUCHERS: BasketVoucher[] = [];

const formatNegativeCurrency = (locale: string, currencyCode: string, minorUnits: number) =>
  `−${formatCurrency(minorUnits, currencyCode, { locale })}`;

export const useDiscountCodes = (
  language: string
): { promoCode: DiscountCodeProps; voucherCode: DiscountCodeProps } => {
  const { t } = useTranslations();
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, AnyAction>>();
  const property = useSelector(entitySelectors.getProperty) as Property;
  const basketDetails = useSelector(basketSelectors.getBasketDetails) as BasketDetails;
  const promoCodeFailure = useSelector(basketSelectors.promoCodeFailure) as boolean;

  const [promoValue, setPromoValue] = useState('');
  const [voucherValue, setVoucherValue] = useState('');
  const [voucherError, setVoucherError] = useState<TranslationKey | null>(null);

  const refetchBasket = useCallback(async () => {
    if (!property?.id || !basketDetails?.id) return;
    await dispatch(fetchBasketDetails(property.id, basketDetails.id, language));
  }, [property?.id, basketDetails?.id, language, dispatch]);

  const applyPromo = useApplyPromoCode(property, basketDetails, (propertyId, basketId, code) =>
    dispatch(applyPromoCode(propertyId, basketId, code))
  );
  const applyVoucher = useApplyVoucherCode(
    property?.id,
    basketDetails?.id,
    refetchBasket,
    setVoucherError
  );
  const removeVoucher = useRemoveVoucherCode(
    property?.id,
    basketDetails?.id,
    refetchBasket,
    setVoucherError
  );

  const resetCrossErrors = useCallback(() => {
    setVoucherError(null);
    if (promoCodeFailure) dispatch(clearPromoCodeFailure());
  }, [promoCodeFailure, dispatch]);

  const appliedPromo = basketDetails?.activePromo;
  const appliedVouchers = basketDetails?.vouchers ?? EMPTY_VOUCHERS;

  const promoCode: DiscountCodeProps = useMemo(
    () => ({
      kind: 'promo',
      value: promoValue,
      onChange: (next: string) => {
        setPromoValue(next);
        resetCrossErrors();
      },
      onApply: () => applyPromo.mutate(promoValue),
      onRemove: () => applyPromo.mutate(''),
      applied: appliedPromo
        ? [
            {
              code: appliedPromo.name,
              amount:
                appliedPromo.discountValue.type === 'percentage'
                  ? `−${appliedPromo.discountValue.value}%`
                  : formatNegativeCurrency(
                      language,
                      appliedPromo.discountValue.value.currencyCode,
                      appliedPromo.discountValue.value.amount
                    ),
            },
          ]
        : undefined,
      successMessage:
        applyPromo.isSuccess && appliedPromo
          ? t('basket.promoCodeApplied', { code: appliedPromo.name })
          : undefined,
      applyLabel: t('basket.apply'),
      removeLabel: t('basket.removePromoCode'),
      placeholder: t('basket.promotionalCode'),
      error: promoCodeFailure ? t('basket.codeError') : undefined,
      disabled: applyPromo.isPending || Boolean(appliedPromo),
    }),
    [promoValue, appliedPromo, applyPromo, t, promoCodeFailure, language, resetCrossErrors]
  );

  const voucherCode: DiscountCodeProps = useMemo(
    () => ({
      kind: 'voucher',
      value: voucherValue,
      onApply: () => {
        setVoucherError(null);
        applyVoucher.mutate(voucherValue, {
          onSuccess: () => setVoucherValue(''),
        });
      },
      onChange: (next: string) => {
        setVoucherValue(next);
        applyVoucher.reset();
        resetCrossErrors();
      },
      onRemove: (code: string) => {
        applyVoucher.reset();
        resetCrossErrors();
        removeVoucher.mutate(code);
      },
      applied:
        appliedVouchers.length > 0
          ? appliedVouchers.map((v) => ({
              code: v.code,
              amount: formatNegativeCurrency(
                language,
                v.redemptionTotal.currencyCode,
                v.redemptionTotal.amount
              ),
            }))
          : undefined,
      successMessage:
        applyVoucher.isSuccess &&
        applyVoucher.variables &&
        appliedVouchers.some((v) => v.code === applyVoucher.variables)
          ? t('basket.voucherCodeApplied', { code: applyVoucher.variables })
          : undefined,
      applyLabel: t('basket.apply'),
      removeLabel: t('common.remove'),
      placeholder: t('basket.voucherCode'),
      error: voucherError ? t(voucherError) : undefined,
      disabled: applyVoucher.isPending || removeVoucher.isPending,
    }),
    [
      voucherValue,
      appliedVouchers,
      applyVoucher,
      removeVoucher,
      t,
      voucherError,
      language,
      resetCrossErrors,
    ]
  );

  return { promoCode, voucherCode };
};
